import {
  DoctorListItem,
  DoctorVisitSummaryResponse,
  DoctorVisitSummaryApiResponse,
} from '../types/doctorTypes';
import { AIInsightsData } from '../types';
import { getErrorMessage } from '../utils/errorMessage';

// Use REACT_APP_API_BASE_URL from .env file
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL ?? process.env.API_BASE_URL ?? '').replace(/\/$/, '');

export const doctorService = {
  async getAllDoctors(companyCode: string, regionCode: string): Promise<DoctorListItem[]> {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL is not configured. Please set REACT_APP_API_BASE_URL in your .env file');
    }

    const apiUrl = `${API_BASE_URL}/api/v1/doctor360-dashboard/get-all-doctors/${companyCode}/${regionCode}`;
    
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Failed to fetch doctors: ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      
      // Handle different response structures
      // If response has List property (like encrypt-ssid response), use it
      if (data.List && Array.isArray(data.List)) {
        return data.List;
      }
      // If response is directly an array, return it
      if (Array.isArray(data)) {
        return data;
      }
      // If response has data property with array, use it
      if (data.data && Array.isArray(data.data)) {
        return data.data;
      }
      // Default: return empty array if structure is unexpected
      return [];
    } catch (error: unknown) {
      console.error('Fetch error:', getErrorMessage(error));
      throw error instanceof Error ? error : new Error(getErrorMessage(error));
    }
  },

  async getDoctorVisitSummary(companyCode: string, customerCode: string, month: string): Promise<DoctorVisitSummaryResponse | null> {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL is not configured. Please set REACT_APP_API_BASE_URL in your .env file');
    }

    const apiUrl = `${API_BASE_URL}/api/v1/doctor360-dashboard/doctor-visit-summary/${companyCode}`;
    
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Customer_Code: customerCode,
          Month: month,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Failed to fetch doctor visit summary: ${response.statusText} - ${errorText}`);
      }

      const data: DoctorVisitSummaryApiResponse = await response.json();
      
      // Return the first item from List, or null if empty
      if (data.List && data.List.length > 0) {
        return data.List[0];
      }
      
      return null;
    } catch (error: unknown) {
      console.error('Fetch error:', getErrorMessage(error));
      throw error instanceof Error ? error : new Error(getErrorMessage(error));
    }
  },

  async getAIInsights(companyCode: string, customerCode: string, month: string): Promise<AIInsightsData | null> {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL is not configured. Please set REACT_APP_API_BASE_URL in your .env file');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/doctor360-dashboard/ai-insights/${companyCode}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Customer_Code: customerCode,
          Month: month,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch AI insights: ${response.statusText} - ${errorText}`);
      }

      const data: { List?: AIInsightsData[] } = await response.json();
      if (data.List && data.List.length > 0) {
        return data.List[0];
      }
      return null;
    } catch (error: unknown) {
      console.error('Fetch error:', getErrorMessage(error));
      throw error instanceof Error ? error : new Error(getErrorMessage(error));
    }
  },
};
